import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
  signal,
} from '@angular/core';
import {
  IGateway,
  IGeoJSONPoint,
  IMetricasGateway,
  IMetricasGateways,
} from 'modelos/src';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { MapCardConfig } from '../../../../../../componentes/map-card/map-card.component';
import { TonoEstado } from '../../../../../../componentes/detalle/detalle.components';
import { duracion } from '../../../../../../auxiliares/estado-conexion.util';
import {
  AvisoGateway,
  avisos,
  envioStats,
  ocupacion,
  textoRatio,
  toaUltimaHora,
  trafico,
} from '../resumen-gateway';

/** Lo que muestra la pestaña Info, ya armado para la plantilla. */
interface VistaGateway {
  envio: { tono: TonoEstado; etiqueta: string; detalle?: string };
  ocupacion: { tono: TonoEstado; etiqueta: string };
  /** ToA de la última hora, debajo de la ocupación. */
  toa?: string;
  /** Sin métricas todavía, el tráfico no se muestra (no es 0). */
  trafico?: {
    uplinks: string;
    downlinks: string;
    ratio: string;
    erroresTx: string;
    conErrores: boolean;
  };
  avisos: AvisoGateway[];
  eui?: string;
  nombreChirpstack?: string;
  descripcion?: string;
  fechaCreacion?: string;
  fechaChirpstack?: string;
  intervaloStats?: string;
}

@Component({
  selector: 'app-detalles-gateway-info',
  templateUrl: './detalles-info.component.html',
  styleUrl: './detalles-info.component.scss',
  standalone: false,
})
export class DetallesGatewayInfoComponent implements OnChanges, OnDestroy {
  /** Los pide la página: el gateway, las métricas de 15 días y el airtime. */
  @Input() gateway?: IGateway;
  @Input() metricas?: IMetricasGateways;
  @Input() airtime?: IMetricasGateway[];

  public vista?: VistaGateway;
  public mapCardConfig: MapCardConfig = {
    title: 'Ubicación',
    icon: 'location_on',
    color: 'red',
  };

  /** Ver `pantallaAncha` y `altoMapa` en el detalle de tracker. */
  public pantallaAncha = signal(window.innerWidth > 900);
  public altoMapa = signal<number | null>(null);
  private observadorMapa?: ResizeObserver;

  @ViewChild('contenedorMapa') set contenedorMapa(
    ref: ElementRef<HTMLElement> | undefined,
  ) {
    this.observadorMapa?.disconnect();
    if (!ref) return;
    this.observadorMapa = new ResizeObserver(([entrada]) => {
      this.altoMapa.set(Math.round(entrada.contentRect.height) || null);
    });
    this.observadorMapa.observe(ref.nativeElement);
  }

  @HostListener('window:resize')
  public alRedimensionar(): void {
    this.pantallaAncha.set(window.innerWidth > 900);
  }

  private armarVista(): void {
    const g = this.gateway;
    if (!g) return;
    const t = this.metricas ? trafico(this.metricas) : undefined;
    const toa = toaUltimaHora(this.airtime);
    const numero = (n: number) => n.toLocaleString('es-AR');

    this.vista = {
      envio: envioStats(g),
      ocupacion: ocupacion(g),
      toa: toa !== null ? `ToA de la última hora: ${toa} seg` : undefined,
      trafico: t
        ? {
            uplinks: numero(t.uplinks),
            downlinks: numero(t.downlinks),
            ratio: textoRatio(t),
            erroresTx: numero(t.erroresTx),
            conErrores: t.erroresTx > 0,
          }
        : undefined,
      avisos: t ? avisos(t, toa) : [],
      eui: g.gatewayEui,
      nombreChirpstack: g.nombreChirpstack,
      descripcion: g.description,
      fechaCreacion: g.fechaCreacion
        ? HelperService.getFechaYHora(g.fechaCreacion)
        : undefined,
      fechaChirpstack: g.fechaCreacionChirpstack
        ? HelperService.getFechaYHora(g.fechaCreacionChirpstack)
        : undefined,
      intervaloStats: g.statsInterval
        ? g.statsInterval < 60
          ? `cada ${g.statsInterval} seg`
          : `cada ${duracion(Math.round(g.statsInterval / 60))}`
        : undefined,
    };
  }

  private armarMapa(): void {
    const c = (this.gateway?.ubicacion as IGeoJSONPoint | undefined)
      ?.coordinates;
    if (!c) {
      this.mapCardConfig = {
        title: 'Ubicación',
        icon: 'location_on',
        color: 'red',
        mapOptions: undefined,
      };
      return;
    }
    const coor: [number, number] = [c[0], c[1]];
    this.mapCardConfig = {
      title: 'Ubicación',
      // Latitud y longitud con 5 decimales (~1 m), antes iban crudas
      subtitle: `${c[1].toFixed(5)}, ${c[0].toFixed(5)}`,
      icon: 'location_on',
      color: 'red',
      mapOptions: {
        markers: [
          {
            coor,
            label: 'gateway',
            data: this.gateway,
            text: this.gateway?.nombre || 'Gateway',
          },
        ],
        center: coor,
        zoom: 15,
      },
    };
  }

  ngOnChanges(): void {
    // Se rearma con cada cambio (el gateway editado, las métricas que llegan)
    this.armarVista();
    this.armarMapa();
  }

  ngOnDestroy(): void {
    this.observadorMapa?.disconnect();
  }
}
